import CustomModal from '@components/CustomModal';
import WolfDropdown from '@components/ui/WolfDropdown';
import WolfTextarea from '@components/ui/WolfTextarea';
import { graphQlClient } from '@config/graphqlClient';
import FeedbackGraphQL from '@graphql/feedback.queries';
import { useTranslation } from '@hooks/useTranslation';
import { SnackType } from '@models/common.model';
import { CreateFeedbackInput, createFeedbackRequest, FeedbackType } from '@models/feedback.model';
import StarIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAppSelector } from '@redux/hooks';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { RootState } from '@redux/store';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const StarContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  gap: '4px',
});

const InteractiveStar = styled('div')<{ clickable?: boolean }>(({ clickable }) => ({
  cursor: clickable ? 'pointer' : 'default',
  transition: 'transform 0.2s',
  '&:hover': clickable
    ? {
        transform: 'scale(1.2)',
      }
    : {},
}));

const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose }) => {

  const localeFeedback = useTranslation('feedback');
  const localeCommon = useTranslation('common');
  const localeActionMessages = useTranslation('actionMessages');
  const dispatch = useDispatch();


  const feedbackTypes = [
    { text: localeFeedback['typeBug'], value: FeedbackType.PROBLEM },
    { text: localeFeedback['typeComment'], value: FeedbackType.COMMENT },
    { text: localeFeedback['typeSuggestion'], value: FeedbackType.SUGGESTION },
    { text: localeFeedback['typeOther'], value: FeedbackType.OTHER },
  ];

  

  const [rating, setRating] = useState<number>(0);
  const [hoveredRating, setHoveredRating] = useState<number>(0);
  const [comment, setComment] = useState<string>('');
  const [type, setType] = useState<{text: string, value: FeedbackType}>(feedbackTypes[0]);
  const [saving, setSaving] = useState<boolean>(false);
  const { userId } = useAppSelector((state: RootState) => state.user);

  const handleTypeChange = (value: any) => {
    console.log("value", value)
    setType(value[0])

  }

  const handleSubmit = async () => {
    if (!userId || comment.trim().length < 5) {
      dispatch(
        appendActionMessage({
          message: localeActionMessages['feedbackMinimalLength'],
          type: SnackType.WARNING,
        }),
      );
      return
    }
    setSaving(true);
    try {
      const feedbackInput: CreateFeedbackInput = {
        rating: rating || undefined,
        comment: comment || undefined,
        type: type.value,
        userId
      };

      const data: createFeedbackRequest = await graphQlClient.request(FeedbackGraphQL.mutations.createFeedback, {
        feedback: feedbackInput,
      });

      if (data?.createFeedback) {
        dispatch(
          appendActionMessage({
            message: localeActionMessages['feedbackSubmittedSuccessfully'],
            type: SnackType.SUCCESS,
          }),
        );
        handleClose();
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      dispatch(
        appendActionMessage({
          message: localeActionMessages['requestError'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setRating(0);
    setHoveredRating(0);
    setComment('');
    setType(feedbackTypes[0]);
    setSaving(false);
    onClose();
  };

  const renderStars = () => {
    const stars = [];
    const displayRating = hoveredRating || rating;

    for (let i = 1; i <= 5; i++) {
      stars.push(
        <InteractiveStar
          key={i}
          clickable
          onClick={() => setRating(i)}
          onMouseEnter={() => setHoveredRating(i)}
          onMouseLeave={() => setHoveredRating(0)}>
          {i <= displayRating ? (
            <StarIcon className="text-utilityLemon" fontSize="large" />
          ) : (
            <StarOutlineIcon className="text-utilityLemon" fontSize="large" />
          )}
        </InteractiveStar>,
      );
    }
    return stars;
  };

  const isCommentValid = comment.trim().length >= 5;

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={handleClose}
      title={localeFeedback['title']}
      maxWidth="600px"
      rounded
      showButtons
      customConfirmText={localeCommon['submit']}
      onConfirm={handleSubmit}>
      <Box className="flex flex-col gap-4 py-4">
        <Box>
          <Typography variant="body14semibold" className="mb-2">
            {localeFeedback['ratingLabel']}
          </Typography>
          <StarContainer>{renderStars()}</StarContainer>
          {rating > 0 && (
            <Typography variant="body12" color="textSecondary" className="mt-1">
              {rating} / 5
            </Typography>
          )}
        </Box>

        <Box>
          <WolfDropdown
            label={type.text || localeFeedback['typeLabel']}
            items={feedbackTypes}
            initialSelectedItems={[type.value]}
            singleSelect={true}
            onChange={handleTypeChange}
          />
        </Box>

        <Box>
          <WolfTextarea
            label={localeFeedback['commentLabel']}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={localeFeedback['commentPlaceholder']}
            rows={5}
          />
          <Box className="flex justify-between mt-1">
            <Typography variant="body12" color={!isCommentValid && comment.length > 0 ? 'error' : 'textSecondary'}>
              {!isCommentValid && comment.length > 0 ? localeFeedback['minCommentLength'] : ''}
            </Typography>
            <Typography variant="body12" color="textSecondary">
              {comment.length} / 1000
            </Typography>
          </Box>
        </Box>
      </Box>
    </CustomModal>
  );
};

export default FeedbackModal;
